import { useCallback, useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Payroll.css';

function Payroll() {
  const { hasPermission, user } = useAuth();
  const [activeTab, setActiveTab] = useState('payroll');
  const [payrolls, setPayrolls] = useState([]);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalGrossSalary: 0,
    totalDeductions: 0,
    totalNetSalary: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Data states
  const [rules, setRules] = useState([]);
  const [structures, setStructures] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [paidPayslips, setPaidPayslips] = useState([]);

  // Modal states
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewEmployeeData, setViewEmployeeData] = useState(null);
  const [showDetailedAttendanceModal, setShowDetailedAttendanceModal] = useState(false);
  const [detailedAttendanceData, setDetailedAttendanceData] = useState(null);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [bankFormData, setBankFormData] = useState({
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    panNumber: '',
    upiId: ''
  });

  // Form data states
  const [ruleFormData, setRuleFormData] = useState({
    ruleType: '',
    threshold: '',
    penaltyType: '',
    penaltyAmount: '',
    status: 'active'
  });

  const [structureFormData, setStructureFormData] = useState({
    employee: '',
    basicSalary: '',
    hra: '',
    allowances: '',
    grossSalary: '',
    lateArrivals: '',
    goalMissed: '',
    uninformedLeave: '',
    penaltyDeduction: ''
  });

  const [employeesWithoutStructure, setEmployeesWithoutStructure] = useState([]);

  // Fetch payroll data and stats
  const fetchPayrolls = useCallback(async () => {
    setLoading(true);
    try {
      const [payrollRes, statsRes] = await Promise.all([
        api.get('/payroll', {
          params: { month: selectedMonth, year: selectedYear }
        }),
        api.get('/payroll/summary/stats', {
          params: { month: selectedMonth, year: selectedYear }
        })
      ]);

      if (payrollRes.data) {
        setPayrolls(payrollRes.data);
      }
      if (statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Error fetching payroll data:', error);
      setPayrolls([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  // Fetch salary rules
  const fetchRules = useCallback(async () => {
    try {
      const response = await api.get('/salary-rules');
      if (response.data) {
        setRules(response.data);
      }
    } catch (error) {
      console.error('Error fetching rules:', error);
    }
  }, []);

  // Fetch salary structures
  const fetchStructures = useCallback(async () => {
    try {
      const response = await api.get('/salary-structure');
      if (response.data) {
        setStructures(response.data);
      }
    } catch (error) {
      console.error('Error fetching structures:', error);
    }
  }, []);

  // Fetch all employees with bank details for HR verification
  const fetchAllEmployees = useCallback(async () => {
    try {
      const response = await api.get('/users/bank-details-list');
      if (response.data) {
        setAllEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  // Fetch paid payslips
  const fetchPaidPayslips = useCallback(async () => {
    try {
      const response = await api.get('/payroll', {
        params: { month: selectedMonth, year: selectedYear }
      });
      if (response.data) {
        setPaidPayslips(response.data.filter(p => p.status === 'paid'));
      }
    } catch (error) {
      console.error('Error fetching paid payslips:', error);
    }
  }, [selectedMonth, selectedYear]);

  // Fetch employees without structure
  const fetchEmployeesWithoutStructure = useCallback(async () => {
    try {
      const response = await api.get('/salary-structure/employees/without-structure');
      if (response.data) {
        setEmployeesWithoutStructure(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  // Fetch preview stats for selected employee
  const fetchPreviewStats = useCallback(async (employeeId) => {
    if (!employeeId) return;
    try {
      const response = await api.get(`/payroll/preview/${employeeId}`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      if (response.data) {
        setStructureFormData(prev => ({
          ...prev,
          lateArrivals: response.data.lateArrivals.toString(),
          goalMissed: response.data.goalMissed.toString(),
          uninformedLeave: response.data.uninformedLeave.toString()
        }));
      }
    } catch (error) {
      console.error('Error fetching preview stats:', error);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (structureFormData.employee && showStructureModal) {
      const isAutoFetch = !selectedStructure; // Only auto-fetch if creating new
      if (isAutoFetch) {
        fetchPreviewStats(structureFormData.employee);
      }
    }
  }, [structureFormData.employee, showStructureModal, fetchPreviewStats, selectedStructure]);

  // Auto-calculate penalty deduction when salary or penalty days change
  useEffect(() => {
    if (showStructureModal) {
      const basic = parseFloat(structureFormData.basicSalary) || 0;
      // Re-calculate allowances/gross locally to ensure we have latest values
      // Note: This logic duplicates the onChange logic for basic salary slightly but ensures consistency
      const hra = Math.round(basic * 0.3);
      const allowances = Math.round(basic * 0.3);
      const gross = basic; // User requested Gross = Basic (no allowances)

      const late = parseFloat(structureFormData.lateArrivals) || 0;
      const goals = parseFloat(structureFormData.goalMissed) || 0;
      const leave = parseFloat(structureFormData.uninformedLeave) || 0;

      const penaltyDays = (late * 0.5) + (goals * 1) + (leave * 1);

      if (basic > 0) {
        const perDaySalary = basic / 30; // Standard 30 days based on BASIC
        const deduction = Math.round(penaltyDays * perDaySalary);

        setStructureFormData(prev => ({
          ...prev,
          penaltyDeduction: deduction.toString()
        }));
      }
    }
  }, [
    structureFormData.basicSalary,
    structureFormData.lateArrivals,
    structureFormData.goalMissed,
    structureFormData.uninformedLeave,
    showStructureModal
  ]);

  useEffect(() => {
    if (hasPermission('payroll', 'read')) {
      fetchPayrolls();
      fetchRules();
      fetchStructures();
      fetchEmployeesWithoutStructure();
      fetchAllEmployees();
      fetchPaidPayslips();
    }
  }, [fetchPayrolls, fetchRules, fetchStructures, fetchEmployeesWithoutStructure, fetchAllEmployees, fetchPaidPayslips, hasPermission]);

  // Calculate and generate payroll
  const handleCalculatePayroll = async () => {
    setLoading(true);
    try {
      const response = await api.post('/payroll/calculate', {
        month: selectedMonth,
        year: selectedYear
      });

      if (response.data) {
        await fetchPayrolls();
      }
    } catch (error) {
      console.error('Error calculating payroll:', error);
      alert('Error calculating payroll. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Process individual payroll
  const handleProcessPayroll = async (payrollId) => {
    try {
      await api.patch(`/payroll/${payrollId}/process`);
      await fetchPayrolls();
    } catch (error) {
      console.error('Error processing payroll:', error);
      alert('Error processing payroll. Please try again.');
    }
  };

  // Mark payroll as paid
  const handleMarkAsPaid = async (payrollId) => {
    if (!window.confirm('Are you sure you want to mark this salary as paid? An email notification will be sent to the employee.')) {
      return;
    }
    try {
      await api.patch(`/payroll/${payrollId}/mark-paid`);
      await fetchPayrolls();
      alert('✅ Salary marked as paid! Email notification sent to employee.');
    } catch (error) {
      console.error('Error marking payroll as paid:', error);
      alert('Error marking payroll as paid. Please try again.');
    }
  };

  // Mark all payrolls as paid for the month
  const handleMarkAllAsPaid = async () => {
    if (!window.confirm(`Are you sure you want to mark ALL salaries as paid for this month? Email notifications will be sent to all employees.`)) {
      return;
    }
    try {
      const response = await api.patch('/payroll/mark-all-paid', {
        month: selectedMonth,
        year: selectedYear
      });
      await fetchPayrolls();
      alert(`✅ ${response.data.count} salaries marked as paid! Email notifications sent to all employees.`);
    } catch (error) {
      console.error('Error marking all payrolls as paid:', error);
      alert('Error marking payrolls as paid. Please try again.');
    }
  };

  // Edit employee bank details
  const handleEditBankDetails = (employee) => {
    setSelectedEmployee(employee);
    setBankFormData({
      bankName: employee.bankName || '',
      accountNumber: employee.accountNumber || '',
      ifscCode: employee.ifscCode || '',
      accountHolderName: employee.accountHolderName || '',
      panNumber: employee.panNumber || '',
      upiId: employee.upiId || ''
    });
    setShowBankModal(true);
  };

  // Save bank details (HR edit - kept for backwards compatibility)
  const handleSaveBankDetails = async () => {
    try {
      await api.put(`/users/${selectedEmployee._id}`, bankFormData);
      alert('✅ Bank details updated successfully!');
      setShowBankModal(false);
      fetchAllEmployees();
    } catch (error) {
      console.error('Error updating bank details:', error);
      alert('Error updating bank details. Please try again.');
    }
  };

  // Verify bank details (HR approve)
  const handleVerifyBankDetails = async (userId) => {
    if (!window.confirm('Are you sure you want to verify and approve these bank details?')) return;
    try {
      await api.patch(`/users/verify-bank-details/${userId}`, { status: 'verified' });
      alert('✅ Bank details verified successfully!');
      fetchAllEmployees();
    } catch (error) {
      console.error('Error verifying bank details:', error);
      alert('Error verifying bank details. Please try again.');
    }
  };

  // Reject bank details (HR reject)
  const handleRejectBankDetails = async (userId) => {
    const reason = prompt('Enter rejection reason (required):');
    if (!reason) {
      alert('Rejection reason is required.');
      return;
    }
    try {
      await api.patch(`/users/verify-bank-details/${userId}`, { status: 'rejected', rejectionReason: reason });
      alert('Bank details rejected. Employee will be notified.');
      fetchAllEmployees();
    } catch (error) {
      console.error('Error rejecting bank details:', error);
      alert('Error rejecting bank details. Please try again.');
    }
  };

  // Finalize payroll
  const handleFinalizePayroll = async (payrollId) => {
    try {
      await api.patch(`/payroll/${payrollId}/finalize`);
      await fetchPayrolls();
      alert('Payroll finalized successfully!');
    } catch (error) {
      console.error('Error finalizing payroll:', error);
      alert('Error finalizing payroll.');
    }
  };

  const handleDeletePayroll = async (payrollId) => {
    if (window.confirm('Are you sure you want to delete this payroll record? This action cannot be undone.')) {
      try {
        await api.delete(`/payroll/${payrollId}`);
        // Optimistically update UI
        setPayrolls(prev => prev.filter(p => p._id !== payrollId));
        alert('Payroll record deleted permanently from database.');
      } catch (error) {
        console.error('Error deleting payroll:', error);
        alert('Error deleting payroll record.');
        fetchPayrolls(); // Revert on error
      }
    }
  };

  const handleClearMonth = async () => {
    const monthName = months.find(m => m.value === selectedMonth)?.label;
    if (window.confirm(`Are you sure you want to delete ALL payroll records for ${monthName} ${selectedYear}? This cannot be undone.`)) {
      try {
        await api.delete('/payroll/clear-period', {
          params: { month: selectedMonth, year: selectedYear }
        });
        setPayrolls([]);
        alert(`All payroll records for ${monthName} ${selectedYear} have been deleted.`);
      } catch (error) {
        console.error('Error clearing month:', error);
        alert('Error clearing payroll records.');
      }
    }
  };

  // Salary Structure Handlers
  const handleSaveRule = async () => {
    try {
      const payload = {
        ruleType: ruleFormData.ruleType,
        threshold: ruleFormData.threshold,
        penaltyType: ruleFormData.penaltyType,
        status: ruleFormData.status,
        description: ruleFormData.description
      };

      if (selectedRule && selectedRule._id) {
        await api.put(`/salary-rules/${selectedRule._id}`, payload);
        alert('Rule updated successfully!');
      } else {
        await api.post('/salary-rules', payload);
        alert('Rule created successfully!');
      }
      setShowRuleModal(false);
      fetchRules();
    } catch (error) {
      console.error('Error saving rule:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.message || 'Error saving salary rule.';
      alert(errorMsg);
    }
  };

  const handleSaveStructure = async () => {
    try {
      // Calculate derived values
      const late = parseFloat(structureFormData.lateArrivals) || 0;
      const goals = parseFloat(structureFormData.goalMissed) || 0;
      const leave = parseFloat(structureFormData.uninformedLeave) || 0;
      const penaltyDays = (late * 0.5) + (goals * 1) + (leave * 1); // Logic: Late=0.5, Others=1

      const payload = {
        employee: structureFormData.employee,
        basicSalary: parseFloat(structureFormData.basicSalary),
        allowances: {
          hra: parseFloat(structureFormData.hra),
          other: parseFloat(structureFormData.allowances)
        },
        deductions: {
          other: 0 // Can be expanded later
        },
        lateArrivals: late,
        goalMissed: goals,
        uninformedLeave: leave,
        totalPenaltyDays: penaltyDays,
        penaltyDeduction: parseFloat(structureFormData.penaltyDeduction) || 0,
        effectiveFrom: new Date()
      };

      if (selectedStructure && selectedStructure._id) {
        await api.put(`/salary-structure/${selectedStructure._id}`, payload);
        alert('Structure updated successfully!');
      } else {
        await api.post('/salary-structure', payload);
        alert('Structure created successfully!');
      }
      setShowStructureModal(false);
      fetchStructures();
    } catch (error) {
      console.error('Error saving structure:', error);
      const errorMsg = error.response?.data?.message || 'Error saving salary structure.';
      alert(errorMsg);
    }
  };

  const handleAddNewRule = () => {
    setSelectedRule(null);
    setRuleFormData({
      ruleType: '',
      threshold: '',
      penaltyType: '',
      status: 'active',
      description: ''
    });
    setShowRuleModal(true);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setRuleFormData({
      ruleType: rule.ruleType,
      threshold: rule.threshold,
      penaltyType: rule.penaltyType,
      status: rule.status,
      description: rule.description || ''
    });
    setShowRuleModal(true);
  };

  const handleDeleteRule = async (ruleId) => {
    if (window.confirm(`Are you sure you want to delete this rule?`)) {
      try {
        await api.delete(`/salary-rules/${ruleId}`);
        alert('Rule deleted successfully!');
        fetchRules();
      } catch (error) {
        console.error('Error deleting rule:', error);
        alert('Error deleting salary rule.');
      }
    }
  };

  const handleAddNewStructure = () => {
    setSelectedStructure(null);
    setStructureFormData({
      employee: '',
      basicSalary: '',
      hra: '',
      allowances: '',
      grossSalary: '',
      lateArrivals: '',
      goalMissed: '',
      uninformedLeave: '',
      penaltyDeduction: ''
    });
    fetchEmployeesWithoutStructure();
    setShowStructureModal(true);
  };

  const handleDeleteStructure = async (structureId) => {
    if (window.confirm(`Are you sure you want to delete this salary structure? This action permanently removes it from the database.`)) {
      try {
        await api.delete(`/salary-structure/${structureId}`);
        alert('Structure deleted successfully!');
        fetchStructures();
        fetchEmployeesWithoutStructure();
      } catch (error) {
        console.error('Error deleting structure:', error);
        alert('Error deleting salary structure.');
      }
    }
  };

  const handleEditStructure = (structure) => {
    setSelectedStructure(structure);
    setStructureFormData({
      employee: structure.employee?._id || structure.employee,
      basicSalary: structure.basicSalary || '',
      hra: structure.allowances?.hra || '',
      allowances: (structure.allowances?.da || 0) + (structure.allowances?.other || 0),
      grossSalary: structure.grossSalary || '',
      lateArrivals: structure.lateArrivals || '',
      goalMissed: structure.goalMissed || '',
      uninformedLeave: structure.uninformedLeave || '',
      penaltyDeduction: structure.penaltyDeduction || ''
    });
    setShowStructureModal(true);
  };

  const handleViewStructure = async (payroll) => {
    setLoadingAttendance(true);
    setShowDetailedAttendanceModal(true);

    try {
      const response = await api.get(`/payroll/employee-attendance/${payroll.employee._id}`, {
        params: { month: selectedMonth, year: selectedYear }
      });
      setDetailedAttendanceData(response.data);
    } catch (error) {
      console.error('Error fetching detailed attendance:', error);
      const errorMessage = error.response?.data?.message || 'Error loading attendance details. Please try again.';
      alert(errorMessage);
      setShowDetailedAttendanceModal(false);
    } finally {
      setLoadingAttendance(false);
    }
  };



  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Helper for PDF: simple formatting without special symbols
  const formatCurrencyForPDF = (amount) => {
    return 'Rs. ' + (amount || 0).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    });
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  // Export Monthly Payroll PDF
  const handleExportPayroll = () => {
    const doc = new jsPDF();
    const monthName = months.find(m => m.value === selectedMonth)?.label;

    doc.setFontSize(18);
    doc.setTextColor(40);
    doc.text(`Monthly Payroll - ${monthName} ${selectedYear}`, 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    const tableColumn = [
      "Employee",
      "Grade",
      "Gross Salary",
      "Present",
      "Late",
      "Penalty Days",
      "Deduction",
      "Net Salary"
    ];

    const tableRows = payrolls.map(payroll => {
      const presentDays = payroll.attendance?.present || 0;
      const incompleteGoals = payroll.performance?.incompleteGoals || 0;
      const lateCheckIns = payroll.attendance?.lateCheckIns || 0;
      const absentDays = payroll.workingDays?.absent || 0;
      const penaltyDays = (lateCheckIns * 0.5) + (incompleteGoals * 1.0) + absentDays;

      return [
        `${payroll.employee?.firstName} ${payroll.employee?.lastName}`,
        payroll.employee?.designation || 'N/A',
        formatCurrencyForPDF(payroll.salaryDetails?.grossSalary),
        presentDays,
        lateCheckIns,
        penaltyDays,
        payroll.salaryDetails?.totalDeductions > 0 ? formatCurrencyForPDF(payroll.salaryDetails.totalDeductions) : 'Rs. 0.00',
        formatCurrencyForPDF(payroll.salaryDetails?.netSalary)
      ];
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      theme: 'grid',
      headStyles: {
        fillColor: [66, 133, 244],
        fontSize: 10,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 'auto' }, // Employee
        1: { cellWidth: 30 }, // Grade
        2: { halign: 'right' }, // Gross
        3: { halign: 'center' }, // Present
        4: { halign: 'center' }, // Late
        5: { halign: 'center' }, // Penalty Days
        6: { halign: 'right', textColor: [220, 38, 38] }, // Deduction
        7: { halign: 'right', fontStyle: 'bold' } // Net
      }
    });

    doc.save(`Payroll_${monthName}_${selectedYear}.pdf`);
  };

  // Export Salary Structures PDF
  const handleExportStructures = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.setTextColor(40);
    doc.text("Employee Salary Structures", 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    const tableColumn = [
      "Employee",
      "Grade",
      "Basic Salary",
      "Late",
      "Goals Missed",
      "Uninformed Leave",
      "Penalty Days",
      "Deduction",
      "Net Salary"
    ];

    const tableRows = structures.map(structure => [
      `${structure.employee?.firstName} ${structure.employee?.lastName}`,
      structure.employee?.designation || 'N/A',
      formatCurrencyForPDF(structure.basicSalary),
      structure.lateArrivals || 0,
      structure.goalMissed || 0,
      structure.uninformedLeave || 0,
      structure.totalPenaltyDays || 0,
      formatCurrencyForPDF(structure.penaltyDeduction || 0),
      formatCurrencyForPDF(structure.netSalary)
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
      theme: 'grid',
      headStyles: {
        fillColor: [16, 185, 129],
        fontSize: 10,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        2: { halign: 'right' }, // Basic
        3: { halign: 'center' }, // Late
        4: { halign: 'center' }, // Goals
        5: { halign: 'center' }, // Leave
        6: { halign: 'center' }, // Penalty
        7: { halign: 'right', textColor: [220, 38, 38] }, // Deduction
        8: { halign: 'right', fontStyle: 'bold' } // Net
      }
    });

    doc.save("Salary_Structures.pdf");
  };

  // Export Payroll to Excel
  const handleExportPayrollExcel = async () => {
    try {
      const response = await api.get('/payroll/export/excel', {
        params: { month: selectedMonth, year: selectedYear },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      const monthName = months.find(m => m.value === selectedMonth)?.label;
      link.download = `Payroll_${monthName}_${selectedYear}.xlsx`;
      link.click();
    } catch (error) {
      console.error('Error exporting payroll to Excel:', error);
      alert('Error exporting Excel file');
    }
  };

  // Export Payroll to Word
  const handleExportPayrollWord = async () => {
    try {
      const response = await api.get('/payroll/export/word', {
        params: { month: selectedMonth, year: selectedYear },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      const monthName = months.find(m => m.value === selectedMonth)?.label;
      link.download = `Payroll_${monthName}_${selectedYear}.docx`;
      link.click();
    } catch (error) {
      console.error('Error exporting payroll to Word:', error);
      alert('Error exporting Word file');
    }
  };

  return (
    <div className="page-container">
      <div className="payroll-container">
        {/* Header Section */}
        <div className="payroll-header">
          <div className="payroll-header-left">
            <h1>Payroll Management</h1>
            <p>Salary calculated based on Attendance, Goals, and Leave types</p>
          </div>
          <div className="payroll-tabs">
            <button
              className={`tab-btn ${activeTab === 'payroll' ? 'active' : ''}`}
              onClick={() => setActiveTab('payroll')}
            >
              Monthly Payroll
            </button>
            <button
              className={`tab-btn ${activeTab === 'salary' ? 'active' : ''}`}
              onClick={() => setActiveTab('salary')}
            >
              Salary Structures
            </button>
            <button
              className={`tab-btn ${activeTab === 'bank' ? 'active' : ''}`}
              onClick={() => setActiveTab('bank')}
            >
              Bank Details
            </button>
            <button
              className={`tab-btn ${activeTab === 'payslips' ? 'active' : ''}`}
              onClick={() => setActiveTab('payslips')}
            >
              Payslips
            </button>
          </div>
        </div>

        {activeTab === 'payroll' && (
          <>
            {/* Filter Section */}
            <div className="payroll-filters">
              <div className="filter-group">
                <label>Month:</label>
                <select
                  className="filter-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Year:</label>
                <select
                  className="filter-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                >
                  {years.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <button className="calculate-btn" onClick={handleCalculatePayroll}>
                Calculate & Generate
              </button>
            </div>

            {/* Stats Section */}
            <div className="payroll-stats">
              <div className="stat-card-payroll blue">
                <h3>{stats.totalEmployees}</h3>
                <p className="stat-label">Employees Paid</p>
              </div>
              <div className="stat-card-payroll green">
                <h3>{formatCurrency(stats.totalGrossSalary)}</h3>
                <p className="stat-label">Base Gross Total</p>
              </div>
              <div className="stat-card-payroll red">
                <h3>{formatCurrency(stats.totalDeductions)}</h3>
                <p className="stat-label">Penalty Deductions</p>
                <p className="stat-sublabel red">(Late In/Absent/Goals)</p>
              </div>
              <div className="stat-card-payroll purple">
                <h3>{formatCurrency(stats.totalNetSalary)}</h3>
                <p className="stat-label">Penalty Deductions</p>
                <p className="stat-sublabel">(Late In/Absent/Goals)</p>
              </div>
            </div>

            {/* Payroll Records Table */}
            <div className="payroll-records">
              <div className="payroll-records-header">
                <h2>
                  Detailed Payroll Records - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="calculate-btn" onClick={handleMarkAllAsPaid} style={{ margin: 0, padding: '6px 16px', fontSize: '12px', background: '#8b5cf6' }}>
                    💰 Mark All Paid
                  </button>
                  <button className="calculate-btn" onClick={handleExportPayrollExcel} style={{ margin: 0, padding: '6px 16px', fontSize: '12px', background: '#10b981' }}>
                    Export Excel
                  </button>
                  <button className="calculate-btn" onClick={handleExportPayrollWord} style={{ margin: 0, padding: '6px 16px', fontSize: '12px', background: '#3b82f6' }}>
                    Export Word
                  </button>
                  <button className="calculate-btn" onClick={handleExportPayroll} style={{ margin: 0, padding: '6px 16px', fontSize: '12px', background: '#4f46e5' }}>
                    Export PDF
                  </button>
                  <button className="calculate-btn" onClick={handleClearMonth} style={{ margin: 0, padding: '6px 16px', fontSize: '12px', background: '#ef4444' }}>
                    Clear Month
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="payroll-loading">
                  <p>Loading payroll data...</p>
                </div>
              ) : payrolls.length === 0 ? (
                <div className="payroll-empty">
                  <h3>No Payroll Records Found</h3>
                  <p>Click "Calculate & Generate" to create payroll for this period</p>
                </div>
              ) : (
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>Employee Name</th>
                      <th>Grade</th>
                      <th>Basic Salary</th>
                      <th>Present Days</th>
                      <th>Goals Completed (Days)</th>
                      <th>Late Check-ins</th>
                      <th>Penalty Days</th>
                      <th>Deduction (₹)</th>
                      <th>Net Salary (₹)</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrolls.map((payroll) => {
                      const presentDays = payroll.attendance?.present || 0;
                      const incompleteGoals = payroll.performance?.incompleteGoals || 0;
                      const goalsCompleted = Math.max(0, presentDays - incompleteGoals);
                      const lateCheckIns = payroll.attendance?.lateCheckIns || 0;
                      const absentDays = payroll.workingDays?.absent || 0;
                      const penaltyDays = (lateCheckIns * 0.5) + (incompleteGoals * 1.0) + absentDays;

                      return (
                        <tr key={payroll._id}>
                          <td>
                            <div className="employee-cell">
                              <span className="employee-name">
                                {payroll.employee?.firstName} {payroll.employee?.lastName}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="grade-text">{payroll.employee?.designation || 'N/A'}</span>
                          </td>
                          <td>
                            <div className="salary-amount">
                              {formatCurrency(payroll.salaryDetails?.grossSalary)}
                            </div>
                          </td>
                          <td>
                            <div className="centered-text">
                              {presentDays}
                            </div>
                          </td>
                          <td>
                            <div className="centered-text">
                              {goalsCompleted}
                            </div>
                          </td>
                          <td>
                            <div className="centered-text">
                              {lateCheckIns}
                            </div>
                          </td>
                          <td>
                            <div className="centered-text property-days">
                              {penaltyDays}
                            </div>
                          </td>
                          <td>
                            <div className="deduction-amount">
                              {payroll.salaryDetails?.totalDeductions > 0 ? formatCurrency(payroll.salaryDetails.totalDeductions) : '₹0'}
                            </div>
                          </td>
                          <td>
                            <div className="net-salary">
                              {formatCurrency(payroll.salaryDetails?.netSalary)}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${payroll.status}`}>
                              {payroll.status === 'paid' ? '✅ Paid' : payroll.status === 'processed' ? '🔄 Processed' : '📝 Draft'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn view"
                                onClick={() => handleViewStructure(payroll)}
                              >
                                View
                              </button>
                              {payroll.status !== 'paid' && (
                                <button
                                  className="action-btn paid"
                                  onClick={() => handleMarkAsPaid(payroll._id)}
                                  style={{ marginLeft: '5px', background: '#10b981' }}
                                >
                                  Paid
                                </button>
                              )}
                              <button
                                className="action-btn delete"
                                onClick={() => handleDeletePayroll(payroll._id)}
                                style={{ marginLeft: '5px', background: '#ef4444' }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {activeTab === 'salary' && (
          <div className="salary-structures-container">
            {/* Salary Calculation Rules */}
            <div className="payroll-records">
              <div className="payroll-records-header">
                <h2>💰 Salary Calculation Rules</h2>
                <button className="calculate-btn" onClick={handleAddNewRule}>+ Add New Rule</button>
              </div>

              <div className="salary-rules-grid">
                {rules.length === 0 ? (
                  <div className="payroll-empty" style={{ gridColumn: '1-3' }}>
                    <p>No calculation rules defined. Click + Add New Rule to get started.</p>
                  </div>
                ) : (
                  rules.map(rule => (
                    <div key={rule._id} className="salary-rule-card">
                      <div className={`rule-card-header ${rule.ruleType.toLowerCase().replace(' ', '-')}`}>
                        <span className="rule-card-icon">
                          {rule.ruleType === 'Late Check-in' ? '⏰' :
                            rule.ruleType === 'Incomplete Goals' ? '🎯' :
                              rule.ruleType === 'Absence' ? '❌' : '🚫'}
                        </span>
                        <h3>{rule.ruleType}</h3>
                      </div>
                      <div className="rule-card-body">
                        <div className="rule-detail">
                          <span>Criteria/Threshold:</span>
                          <strong>{rule.threshold}</strong>
                        </div>
                        <div className="rule-detail">
                          <span>Penalty:</span>
                          <strong className="red">{rule.penaltyType} Penalty</strong>
                        </div>
                        {rule.description && (
                          <div className="rule-detail">
                            <span style={{ fontSize: '12px', color: '#64748b' }}>{rule.description}</span>
                          </div>
                        )}
                        <div className="rule-detail">
                          <span>Status:</span>
                          <span className={`status-badge ${rule.status === 'active' ? 'active' : 'inactive'}`}>
                            {rule.status.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="rule-card-footer">
                        <button className="btn-edit" onClick={() => handleEditRule(rule)}>Edit</button>
                        <button className="btn-delete" onClick={() => handleDeleteRule(rule._id)}>Delete</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Employee Salary Structures */}
            <div className="payroll-records" style={{ marginTop: '20px' }}>
              <div className="payroll-records-header">
                <h2>👥 Employee Salary Structures by Grade</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button className="calculate-btn" onClick={handleExportStructures} style={{ margin: 0, padding: '6px 16px', fontSize: '12px', background: '#4f46e5' }}>
                    Export PDF
                  </button>
                  <button className="calculate-btn" onClick={handleAddNewStructure}>+ Add New Structure</button>
                </div>
              </div>

              <div className="salary-structures-table">
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Basic Salary</th>
                      <th>Late Arrivals</th>
                      <th>Goal Missed</th>
                      <th>Uninformed Leave</th>
                      <th>Total Penalty Days</th>
                      <th>Deduction</th>
                      <th>Net Salary</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {structures.length === 0 ? (
                      <tr>
                        <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>
                          No salary structures defined. Click + Add New Structure to assign one to an employee.
                        </td>
                      </tr>
                    ) : (
                      structures.map(structure => (
                        <tr key={structure._id}>
                          <td>
                            <div className="employee-cell">
                              <span className="employee-name">{structure.employee?.firstName} {structure.employee?.lastName}</span>
                              <span className="employee-id">{structure.employee?.employeeId}</span>
                              <span className="grade-level" style={{ fontSize: '11px', color: '#94a3b8' }}>{structure.employee?.designation}</span>
                            </div>
                          </td>
                          <td><strong>{formatCurrency(structure.basicSalary)}</strong></td>
                          <td className="centered-text">{structure.lateArrivals || 0}</td>
                          <td className="centered-text">{structure.goalMissed || 0}</td>
                          <td className="centered-text">{structure.uninformedLeave || 0}</td>
                          <td className="centered-text property-days">{structure.totalPenaltyDays || 0}</td>
                          <td className="deduction-amount">
                            {formatCurrency(structure.penaltyDeduction || 0)}
                          </td>
                          <td><strong className="green">{formatCurrency(structure.netSalary)}</strong></td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button className="btn-edit" onClick={() => handleEditStructure(structure)}>Edit</button>
                              <button className="btn-delete" onClick={() => handleDeleteStructure(structure._id)}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Bank Details Tab */}
        {activeTab === 'bank' && (
          <div className="salary-structures-container">
            {/* Stats Summary */}
            <div className="payroll-stats" style={{ marginBottom: '20px' }}>
              <div className="stat-card-payroll blue">
                <h3>{allEmployees.length}</h3>
                <p className="stat-label">Total Employees</p>
              </div>
              <div className="stat-card-payroll green">
                <h3>{allEmployees.filter(e => e.bankDetailsStatus === 'verified').length}</h3>
                <p className="stat-label">Verified</p>
              </div>
              <div className="stat-card-payroll" style={{ background: '#fef3c7' }}>
                <h3 style={{ color: '#92400e' }}>{allEmployees.filter(e => e.bankDetailsStatus === 'pending').length}</h3>
                <p className="stat-label" style={{ color: '#92400e' }}>Pending Verification</p>
              </div>
              <div className="stat-card-payroll red">
                <h3>{allEmployees.filter(e => e.bankDetailsStatus === 'rejected').length}</h3>
                <p className="stat-label">Rejected</p>
              </div>
            </div>

            <div className="payroll-records">
              <div className="payroll-records-header">
                <h2>🏦 Employee Bank Details Verification</h2>
                <p style={{ color: '#64748b', fontSize: '14px' }}>Review and verify employee bank details submitted for salary transfers</p>
              </div>

              <div className="salary-structures-table">
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Bank Name</th>
                      <th>Account Number</th>
                      <th>IFSC Code</th>
                      <th>UPI/QR Code</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allEmployees.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                          No employees found.
                        </td>
                      </tr>
                    ) : (
                      allEmployees.map(employee => (
                        <tr key={employee._id}>
                          <td>
                            <div className="employee-cell">
                              <span className="employee-name">{employee.firstName} {employee.lastName}</span>
                              <span className="employee-id">{employee.employeeId}</span>
                              <span className="grade-level" style={{ fontSize: '11px', color: '#94a3b8' }}>{employee.designation}</span>
                            </div>
                          </td>
                          <td>{employee.bankName || <span style={{ color: '#94a3b8' }}>Not Set</span>}</td>
                          <td>{employee.accountNumber || <span style={{ color: '#94a3b8' }}>Not Set</span>}</td>
                          <td>{employee.ifscCode || <span style={{ color: '#94a3b8' }}>Not Set</span>}</td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {employee.upiId && (
                                <span style={{ fontSize: '12px' }}>📱 {employee.upiId}</span>
                              )}
                              {employee.paymentQRCode ? (
                                <button
                                  onClick={() => {
                                    setSelectedEmployee(employee);
                                    document.getElementById('qr-preview-modal').style.display = 'flex';
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    background: '#8b5cf6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  📷 View QR
                                </button>
                              ) : (
                                !employee.upiId && <span style={{ color: '#94a3b8', fontSize: '12px' }}>Not Set</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className={`status-badge ${employee.bankDetailsStatus}`} style={{
                              background: employee.bankDetailsStatus === 'verified' ? '#dcfce7' :
                                employee.bankDetailsStatus === 'rejected' ? '#fee2e2' : '#fef3c7',
                              color: employee.bankDetailsStatus === 'verified' ? '#166534' :
                                employee.bankDetailsStatus === 'rejected' ? '#991b1b' : '#92400e'
                            }}>
                              {employee.bankDetailsStatus === 'verified' ? '✅ Verified' :
                                employee.bankDetailsStatus === 'rejected' ? '❌ Rejected' : '⏳ Pending'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              <button className="btn-edit" onClick={() => handleEditBankDetails(employee)} style={{ padding: '5px 10px', fontSize: '12px' }}>
                                👁️ View
                              </button>
                              {employee.bankDetailsStatus === 'pending' && employee.bankName && (
                                <>
                                  <button 
                                    onClick={() => handleVerifyBankDetails(employee._id)}
                                    style={{ 
                                      padding: '5px 10px', 
                                      fontSize: '12px', 
                                      background: '#10b981', 
                                      color: 'white', 
                                      border: 'none', 
                                      borderRadius: '5px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    ✅ Verify
                                  </button>
                                  <button 
                                    onClick={() => handleRejectBankDetails(employee._id)}
                                    style={{ 
                                      padding: '5px 10px', 
                                      fontSize: '12px', 
                                      background: '#ef4444', 
                                      color: 'white', 
                                      border: 'none', 
                                      borderRadius: '5px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    ❌ Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Payslips Tab */}
        {activeTab === 'payslips' && (
          <div className="salary-structures-container">
            <div className="payroll-records">
              <div className="payroll-records-header">
                <h2>📄 Paid Payslips - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}</h2>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <div className="filter-group" style={{ marginBottom: 0 }}>
                    <select
                      className="filter-select"
                      value={selectedMonth}
                      onChange={(e) => {
                        setSelectedMonth(parseInt(e.target.value));
                        fetchPaidPayslips();
                      }}
                    >
                      {months.map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="filter-group" style={{ marginBottom: 0 }}>
                    <select
                      className="filter-select"
                      value={selectedYear}
                      onChange={(e) => {
                        setSelectedYear(parseInt(e.target.value));
                        fetchPaidPayslips();
                      }}
                    >
                      {years.map(year => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="salary-structures-table">
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Gross Salary</th>
                      <th>Deductions</th>
                      <th>Net Salary</th>
                      <th>Payment Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paidPayslips.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                          No paid payslips for this month. Use the Monthly Payroll tab to mark salaries as paid.
                        </td>
                      </tr>
                    ) : (
                      paidPayslips.map(payslip => (
                        <tr key={payslip._id}>
                          <td>
                            <div className="employee-cell">
                              <span className="employee-name">{payslip.employee?.firstName} {payslip.employee?.lastName}</span>
                              <span className="employee-id">{payslip.employee?.employeeId}</span>
                            </div>
                          </td>
                          <td><strong>{formatCurrency(payslip.salaryDetails?.grossSalary)}</strong></td>
                          <td className="deduction-amount">-{formatCurrency(payslip.salaryDetails?.totalDeductions)}</td>
                          <td><strong className="green">{formatCurrency(payslip.salaryDetails?.netSalary)}</strong></td>
                          <td>{payslip.paidAt ? new Date(payslip.paidAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}</td>
                          <td>
                            <span className="status-badge paid" style={{ background: '#dcfce7', color: '#166534' }}>
                              ✅ Paid
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Summary Stats */}
              {paidPayslips.length > 0 && (
                <div className="payroll-stats" style={{ marginTop: '20px' }}>
                  <div className="stat-card-payroll blue">
                    <h3>{paidPayslips.length}</h3>
                    <p className="stat-label">Employees Paid</p>
                  </div>
                  <div className="stat-card-payroll green">
                    <h3>{formatCurrency(paidPayslips.reduce((sum, p) => sum + (p.salaryDetails?.grossSalary || 0), 0))}</h3>
                    <p className="stat-label">Total Gross</p>
                  </div>
                  <div className="stat-card-payroll red">
                    <h3>{formatCurrency(paidPayslips.reduce((sum, p) => sum + (p.salaryDetails?.totalDeductions || 0), 0))}</h3>
                    <p className="stat-label">Total Deductions</p>
                  </div>
                  <div className="stat-card-payroll purple">
                    <h3>{formatCurrency(paidPayslips.reduce((sum, p) => sum + (p.salaryDetails?.netSalary || 0), 0))}</h3>
                    <p className="stat-label">Total Paid</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bank Details Modal */}
        {showBankModal && (
          <div className="modal-overlay" onClick={() => setShowBankModal(false)}>
            <div className="modal-container" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>🏦 Bank Details - {selectedEmployee?.firstName} {selectedEmployee?.lastName}</h2>
                <button className="modal-close" onClick={() => setShowBankModal(false)}>×</button>
              </div>

              <div className="modal-body">
                {/* Status Banner */}
                {selectedEmployee && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    background: selectedEmployee.bankDetailsStatus === 'verified' ? '#dcfce7' :
                      selectedEmployee.bankDetailsStatus === 'rejected' ? '#fee2e2' : '#fef3c7',
                    color: selectedEmployee.bankDetailsStatus === 'verified' ? '#166534' :
                      selectedEmployee.bankDetailsStatus === 'rejected' ? '#991b1b' : '#92400e'
                  }}>
                    <strong>Status: </strong>
                    {selectedEmployee.bankDetailsStatus === 'verified' ? '✅ Verified' :
                      selectedEmployee.bankDetailsStatus === 'rejected' ? '❌ Rejected' : '⏳ Pending Verification'}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      placeholder="e.g., State Bank of India"
                      value={bankFormData.bankName}
                      onChange={(e) => setBankFormData({ ...bankFormData, bankName: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Number</label>
                    <input
                      type="text"
                      placeholder="Enter account number"
                      value={bankFormData.accountNumber}
                      onChange={(e) => setBankFormData({ ...bankFormData, accountNumber: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>IFSC Code</label>
                    <input
                      type="text"
                      placeholder="e.g., SBIN0001234"
                      value={bankFormData.ifscCode}
                      onChange={(e) => setBankFormData({ ...bankFormData, ifscCode: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Account Holder Name</label>
                    <input
                      type="text"
                      placeholder="Enter account holder name"
                      value={bankFormData.accountHolderName}
                      onChange={(e) => setBankFormData({ ...bankFormData, accountHolderName: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>PAN Number</label>
                    <input
                      type="text"
                      placeholder="e.g., ABCDE1234F"
                      value={bankFormData.panNumber}
                      onChange={(e) => setBankFormData({ ...bankFormData, panNumber: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div className="form-group">
                    <label>UPI ID</label>
                    <input
                      type="text"
                      placeholder="e.g., name@upi"
                      value={bankFormData.upiId}
                      onChange={(e) => setBankFormData({ ...bankFormData, upiId: e.target.value })}
                    />
                  </div>
                </div>

                {/* Payment QR Code Section */}
                <div style={{
                  marginTop: '20px',
                  padding: '20px',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '2px dashed #cbd5e1'
                }}>
                  <h4 style={{ margin: '0 0 15px', color: '#334155' }}>
                    📱 Payment QR Code / Barcode
                  </h4>
                  
                  {selectedEmployee?.paymentQRCode ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                      <div style={{
                        width: '150px',
                        height: '150px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        background: 'white'
                      }}>
                        <img
                          src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${selectedEmployee.paymentQRCode}`}
                          alt="Payment QR Code"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8">No Image</text></svg>'; }}
                        />
                      </div>
                      <div>
                        <p style={{ color: '#10b981', fontWeight: '600', marginBottom: '10px' }}>✅ QR Code Available</p>
                        <p style={{ fontSize: '12px', color: '#64748b' }}>
                          Use this QR code for quick salary transfer via UPI
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                      <p style={{ fontSize: '48px', marginBottom: '10px' }}>📷</p>
                      <p>Employee has not uploaded a QR code</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="modal-btn modal-btn-secondary" onClick={() => setShowBankModal(false)}>
                  Cancel
                </button>
                <button className="modal-btn modal-btn-primary" onClick={handleSaveBankDetails}>
                  💾 Save Bank Details
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rule Modal */}
        {showRuleModal && (
          <div className="modal-overlay" onClick={() => setShowRuleModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedRule ? `Edit ${selectedRule} Rule` : 'Add New Salary Rule'}</h2>
                <button className="modal-close" onClick={() => setShowRuleModal(false)}>×</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label>Rule Type <span>*</span></label>
                  <select
                    value={ruleFormData.ruleType}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, ruleType: e.target.value })}
                  >
                    <option value="">Select Rule Type</option>
                    <option value="Late Check-in">Late Check-in Penalty</option>
                    <option value="Incomplete Goals">Incomplete Daily Goals</option>
                    <option value="Absence">Absence Penalty</option>
                    <option value="Uninformed Leave">Uninformed Leave Penalty</option>
                  </select>
                  <div className="form-hint">Choose the type of salary calculation rule</div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Threshold/Criteria</label>
                    <input
                      type="text"
                      placeholder="e.g., 10:10 AM"
                      value={ruleFormData.threshold}
                      onChange={(e) => setRuleFormData({ ...ruleFormData, threshold: e.target.value })}
                    />
                    <div className="form-hint">Time or condition for rule application</div>
                  </div>

                  <div className="form-group">
                    <label>Status <span>*</span></label>
                    <select
                      value={ruleFormData.status}
                      onChange={(e) => setRuleFormData({ ...ruleFormData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Penalty Type <span>*</span></label>
                  <select
                    value={ruleFormData.penaltyType}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, penaltyType: e.target.value })}
                  >
                    <option value="">Select Penalty Type</option>
                    <option value="Half-day">Half-day Attendance (0.5 day deduction)</option>
                    <option value="Full-day">Full-day Absence (marked absent)</option>
                    <option value="3-Day">3-Day Absence Penalty</option>
                    <option value="Per-day">Per-day Salary Deduction</option>
                  </select>
                  <div className="form-hint">How the penalty will be calculated</div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Additional notes about this rule..."
                    value={ruleFormData.description || ''}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, description: e.target.value })}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button className="modal-btn modal-btn-secondary" onClick={() => setShowRuleModal(false)}>
                  Cancel
                </button>
                <button className="modal-btn modal-btn-primary" onClick={handleSaveRule}>
                  {selectedRule ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Structure Modal */}
        {showStructureModal && (
          <div className="modal-overlay" onClick={() => setShowStructureModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedStructure ? 'Edit Salary Structure' : 'Add New Salary Structure'}</h2>
                <button className="modal-close" onClick={() => setShowStructureModal(false)}>×</button>
              </div>

              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Select Employee <span>*</span></label>
                    <select
                      value={structureFormData.employee}
                      onChange={(e) => setStructureFormData({ ...structureFormData, employee: e.target.value })}
                      disabled={!!selectedStructure}
                    >
                      <option value="">Select Employee</option>
                      {selectedStructure ? (
                        <option value={selectedStructure.employee?._id}>
                          {selectedStructure.employee?.firstName} {selectedStructure.employee?.lastName}
                        </option>
                      ) : (
                        employeesWithoutStructure.map(emp => (
                          <option key={emp._id} value={emp._id}>
                            {emp.firstName} {emp.lastName} ({emp.employeeId})
                          </option>
                        ))
                      )}
                    </select>
                    <div className="form-hint">Assign salary structure to an employee</div>
                  </div>
                  <div className="form-group">
                    <label>Grade (Designation)</label>
                    <input
                      type="text"
                      value={(() => {
                        const empId = structureFormData.employee;
                        if (!empId) return '';
                        if (selectedStructure && selectedStructure.employee?._id === empId) {
                          return selectedStructure.employee.designation || 'N/A';
                        }
                        const emp = employeesWithoutStructure.find(e => e._id === empId);
                        return emp ? emp.designation : 'N/A';
                      })()}
                      disabled
                      style={{ background: '#f1f5f9', color: '#64748b' }}
                    />
                    <div className="form-hint">Employee's grade based on designation</div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Basic Salary <span>*</span></label>
                  <input
                    type="number"
                    placeholder="e.g., 50000"
                    value={structureFormData.basicSalary}
                    onChange={(e) => {
                      const basic = parseFloat(e.target.value) || 0;
                      const hra = Math.round(basic * 0.3);
                      const allowances = Math.round(basic * 0.3);
                      const gross = basic; // User requested Gross = Basic
                      setStructureFormData({
                        ...structureFormData,
                        basicSalary: e.target.value,
                        hra: hra.toString(),
                        allowances: allowances.toString(),
                        grossSalary: gross.toString()
                      });
                    }}
                  />
                  <div className="form-hint">Base salary amount (40-60% of gross)</div>
                </div>

                <div className="breakdown-section" style={{ marginTop: '20px', padding: '15px', background: '#fff0f0', border: '1px solid #fecaca' }}>
                  <h3 style={{ marginBy: '10px', color: '#dc2626' }}>Penalty & Deductions Setup</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Late Arrivals (Days)</label>
                      <input
                        type="number"
                        placeholder="0"
                        value={structureFormData.lateArrivals}
                        disabled
                        style={{ background: '#f1f5f9' }}
                      />
                      <div className="form-hint">Auto-fetched from Attendance</div>
                    </div>
                    <div className="form-group">
                      <label>Goal Missed (Days)</label>
                      <input
                        type="number"
                        placeholder="0"
                        value={structureFormData.goalMissed}
                        disabled
                        style={{ background: '#f1f5f9' }}
                      />
                      <div className="form-hint">Auto-fetched from Goals</div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Uninformed Leave (Days)</label>
                      <input
                        type="number"
                        placeholder="0"
                        value={structureFormData.uninformedLeave}
                        disabled
                        style={{ background: '#f1f5f9' }}
                      />
                      <div className="form-hint">Auto-fetched from Attendance</div>
                    </div>
                    <div className="form-group">
                      <label>Total Penalty Days</label>
                      <input
                        type="number"
                        value={
                          ((parseFloat(structureFormData.lateArrivals) || 0) * 0.5) +
                          ((parseFloat(structureFormData.goalMissed) || 0) * 1) +
                          ((parseFloat(structureFormData.uninformedLeave) || 0) * 1)
                        }
                        disabled
                        style={{ background: '#fef2f2', fontWeight: 'bold', color: '#dc2626' }}
                      />
                      <div className="form-hint">Auto-calculated: Late*0.5 + Goals*1 + Leave*1</div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Deduction Amount (₹)</label>
                    <input
                      type="number"
                      placeholder="e.g. 1500"
                      value={structureFormData.penaltyDeduction}
                      onChange={(e) => setStructureFormData({ ...structureFormData, penaltyDeduction: e.target.value })}
                    />
                    <div className="form-hint">Manual override for penalty deduction amount</div>
                  </div>

                  {/* Calculation Breakdown Display */}
                  {(parseFloat(structureFormData.basicSalary) > 0) && (
                    <div className="calculation-breakdown" style={{ marginTop: '15px', padding: '10px', background: '#fff', border: '1px dashed #cbd5e1', borderRadius: '4px', fontSize: '0.9rem' }}>
                      <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#475569' }}>🧮 Deduction Calculation Logic</h4>

                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span>Daily Rate (Basic / 30):</span>
                        <strong>{formatCurrency(parseFloat(structureFormData.basicSalary) / 30)}</strong>
                      </div>

                      <div style={{ borderTop: '1px solid #e2e8f0', margin: '5px 0' }}></div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
                        <span>Late ({structureFormData.lateArrivals || 0} × 0.5 × Rate):</span>
                        <span>{formatCurrency((parseFloat(structureFormData.lateArrivals) || 0) * 0.5 * (parseFloat(structureFormData.basicSalary) / 30))}</span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
                        <span>Leave ({structureFormData.uninformedLeave || 0} × 1.0 × Rate):</span>
                        <span>{formatCurrency((parseFloat(structureFormData.uninformedLeave) || 0) * 1.0 * (parseFloat(structureFormData.basicSalary) / 30))}</span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b' }}>
                        <span>Goals ({structureFormData.goalMissed || 0} × 1.0 × Rate):</span>
                        <span>{formatCurrency((parseFloat(structureFormData.goalMissed) || 0) * 1.0 * (parseFloat(structureFormData.basicSalary) / 30))}</span>
                      </div>

                      <div style={{ borderTop: '1px solid #e2e8f0', margin: '5px 0' }}></div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                        <span>Total Calculated Deduction:</span>
                        <span className="red">{formatCurrency(parseFloat(structureFormData.penaltyDeduction))}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="modal-btn modal-btn-secondary" onClick={() => setShowStructureModal(false)}>
                  Cancel
                </button>
                <button className="modal-btn modal-btn-primary" onClick={handleSaveStructure}>
                  {selectedStructure ? 'Update Structure' : 'Create Structure'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* View Modal */}
        {showViewModal && viewEmployeeData && (
          <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
            <div className="modal-container" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Employee Summary</h2>
                <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
              </div>

              <div className="modal-body">
                <div className="component-list" style={{ gap: '15px', display: 'flex', flexDirection: 'column' }}>
                  <div className="component-item" style={{ fontSize: '1.1rem', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
                    <span>Employee:</span>
                    <strong>{viewEmployeeData.name}</strong>
                  </div>
                  <div className="component-item">
                    <span>Grade:</span>
                    <span>{viewEmployeeData.grade}</span>
                  </div>
                  <div className="component-item">
                    <span>Gross Salary:</span>
                    <strong>{formatCurrency(viewEmployeeData.gross)}</strong>
                  </div>
                  <div className="component-item">
                    <span>Penalty Days:</span>
                    <span style={{ color: viewEmployeeData.penaltyDays > 0 ? '#ef4444' : '#10b981', fontWeight: '600' }}>
                      {viewEmployeeData.penaltyDays} Days
                    </span>
                  </div>
                  <div className="component-item">
                    <span>Total Deduction:</span>
                    <span style={{ color: '#ef4444', fontWeight: '600' }}>-{formatCurrency(viewEmployeeData.deduction)}</span>
                  </div>
                  <div className="component-item" style={{ marginTop: '10px', paddingTop: '15px', borderTop: '2px solid #e2e8f0' }}>
                    <span style={{ fontSize: '1.2rem' }}>Net Salary:</span>
                    <strong className="green" style={{ fontSize: '1.5rem' }}>{formatCurrency(viewEmployeeData.net)}</strong>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button className="modal-btn modal-btn-secondary" onClick={() => setShowViewModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Attendance Modal */}
        {showDetailedAttendanceModal && (
          <div className="modal-overlay" onClick={() => setShowDetailedAttendanceModal(false)}>
            <div className="modal-container" style={{ maxWidth: '95%', width: '1400px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  {detailedAttendanceData ?
                    `${detailedAttendanceData.employee.name} - Attendance Details` :
                    'Loading...'}
                </h2>
                <button className="modal-close" onClick={() => setShowDetailedAttendanceModal(false)}>×</button>
              </div>

              <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                {loadingAttendance ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Loading attendance details...</p>
                  </div>
                ) : detailedAttendanceData ? (
                  <>
                    {/* Employee Info */}
                    <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                        <div>
                          <strong>Employee ID:</strong> {detailedAttendanceData.employee.employeeId}
                        </div>
                        <div>
                          <strong>Department:</strong> {detailedAttendanceData.employee.department || 'N/A'}
                        </div>
                        <div>
                          <strong>Designation:</strong> {detailedAttendanceData.employee.designation || 'N/A'}
                        </div>
                        <div>
                          <strong>Month/Year:</strong> {months.find(m => m.value === detailedAttendanceData.month)?.label} {detailedAttendanceData.year}
                        </div>
                      </div>
                    </div>

                    {/* Daily Attendance Table */}
                    <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ background: '#286cd2', color: 'white' }}>
                            <th style={{ padding: '8px', border: '1px solid #ddd', minWidth: '40px' }}>Day</th>
                            {detailedAttendanceData.dailyBreakdown.map((day) => (
                              <th key={day.day} style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                minWidth: '50px',
                                background: day.status === 'Absent' ? '#ffebee' : day.status === 'Half-day' ? '#fff3e0' : '#286cd2'
                              }}>
                                {day.day}
                              </th>
                            ))}
                            <th style={{ padding: '8px', border: '1px solid #ddd', minWidth: '80px' }}>Summary</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Active Time Row */}
                          <tr>
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                              Active Time
                            </td>
                            {detailedAttendanceData.dailyBreakdown.map((day) => (
                              <td key={`time-${day.day}`} style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                textAlign: 'center',
                                fontSize: '11px'
                              }}>
                                {day.loginTime ? new Date(day.loginTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                              </td>
                            ))}
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                              {detailedAttendanceData.summary.totalDays}
                            </td>
                          </tr>

                          {/* Dept Time Row */}
                          <tr>
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                              Dept Time
                            </td>
                            {detailedAttendanceData.dailyBreakdown.map((day) => (
                              <td key={`dept-${day.day}`} style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                textAlign: 'center',
                                fontSize: '11px'
                              }}>
                                {day.logoutTime ? new Date(day.logoutTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '-'}
                              </td>
                            ))}
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                              {detailedAttendanceData.summary.presentDays}
                            </td>
                          </tr>

                          {/* Working Hrs Row */}
                          <tr>
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                              Working Hrs
                            </td>
                            {detailedAttendanceData.dailyBreakdown.map((day) => (
                              <td key={`hrs-${day.day}`} style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                textAlign: 'center',
                                fontWeight: '500'
                              }}>
                                {day.workingHours > 0 ? day.workingHours.toFixed(1) : '-'}
                              </td>
                            ))}
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                              {detailedAttendanceData.summary.halfDays}
                            </td>
                          </tr>

                          {/* On Times Hrs Row */}
                          <tr>
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                              On Time/Late
                            </td>
                            {detailedAttendanceData.dailyBreakdown.map((day) => (
                              <td key={`ontime-${day.day}`} style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                textAlign: 'center',
                                background: day.isLate ? '#ffebee' : day.status === 'Present' ? '#e8f5e9' : 'white',
                                fontWeight: '500',
                                fontSize: '11px'
                              }}>
                                {day.status === 'Present' ? (day.isLate ? 'Late' : 'On Time') : '-'}
                              </td>
                            ))}
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold' }}>
                              {detailedAttendanceData.summary.absentDays}
                            </td>
                          </tr>

                          {/* Shift Row */}
                          <tr>
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#f8f9fa' }}>
                              Shift
                            </td>
                            {detailedAttendanceData.dailyBreakdown.map((day) => (
                              <td key={`shift-${day.day}`} style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                textAlign: 'center',
                                background: day.status === 'Present' ? '#e3f2fd' : day.status === 'Absent' ? '#ffebee' : '#fff3e0',
                                fontWeight: 'bold',
                                fontSize: '11px'
                              }}>
                                {day.status === 'Present' ? 'P' : day.status === 'Half-day' ? 'H' : day.status === 'Absent' ? 'A' : 'OFF'}
                              </td>
                            ))}
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontWeight: 'bold', background: '#fff3e0' }}>
                              Late: {detailedAttendanceData.summary.lateDays}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Payroll Summary */}
                    {detailedAttendanceData.payroll && (
                      <div style={{ marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <h3 style={{ marginBottom: '15px', color: '#286cd2' }}>Salary Calculation</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                              <span><strong>Basic Salary:</strong></span>
                              <span style={{ fontWeight: 'bold', fontSize: '18px' }}>{formatCurrency(detailedAttendanceData.payroll.basicSalary)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                              <span>Late Deduction:</span>
                              <span style={{ color: '#ef4444' }}>-{formatCurrency(detailedAttendanceData.payroll.deductionBreakdown.lateDeduction)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                              <span>Goal Deduction:</span>
                              <span style={{ color: '#ef4444' }}>-{formatCurrency(detailedAttendanceData.payroll.deductionBreakdown.goalDeduction)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                              <span>Loss of Pay (Absent):</span>
                              <span style={{ color: '#ef4444' }}>-{formatCurrency(detailedAttendanceData.payroll.deductionBreakdown.lossOfPay)}</span>
                            </div>
                          </div>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', borderBottom: '1px solid #ddd' }}>
                              <span><strong>Total Deductions:</strong></span>
                              <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '18px' }}>-{formatCurrency(detailedAttendanceData.payroll.totalDeductions)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px', background: '#e8f5e9', borderRadius: '8px', marginTop: '10px' }}>
                              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>Net Salary:</span>
                              <span style={{ color: '#10b981', fontWeight: 'bold', fontSize: '24px' }}>{formatCurrency(detailedAttendanceData.payroll.netSalary)}</span>
                            </div>
                            <div style={{ marginTop: '15px', padding: '10px', background: '#fff3e0', borderRadius: '8px' }}>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                <div>Total Working Hours: <strong>{detailedAttendanceData.summary.totalWorkingHours} hrs</strong></div>
                                <div>Present Days: <strong>{detailedAttendanceData.summary.presentDays}</strong></div>
                                <div>Late Days: <strong>{detailedAttendanceData.summary.lateDays}</strong></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>No data available</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="modal-btn modal-btn-secondary" onClick={() => setShowDetailedAttendanceModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* QR Code Preview Modal */}
        <div 
          id="qr-preview-modal"
          onClick={(e) => {
            if (e.target.id === 'qr-preview-modal') {
              document.getElementById('qr-preview-modal').style.display = 'none';
            }
          }}
          style={{
            display: 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 1000,
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#334155' }}>
                📱 Payment QR Code
              </h3>
              <button
                onClick={() => document.getElementById('qr-preview-modal').style.display = 'none'}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#94a3b8'
                }}
              >
                ×
              </button>
            </div>
            
            {selectedEmployee && (
              <>
                <p style={{ textAlign: 'center', marginBottom: '15px', color: '#64748b' }}>
                  {selectedEmployee.firstName} {selectedEmployee.lastName}
                </p>
                
                {selectedEmployee.paymentQRCode ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    background: '#f8fafc',
                    borderRadius: '8px'
                  }}>
                    <img
                      src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${selectedEmployee.paymentQRCode}`}
                      alt="Payment QR Code"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23f1f5f9" width="200" height="200"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="14">Image not found</text></svg>';
                      }}
                    />
                    <p style={{ marginTop: '15px', fontSize: '13px', color: '#10b981' }}>
                      ✅ Scan this QR code for salary transfer
                    </p>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                    No QR code uploaded
                  </div>
                )}

                {selectedEmployee.upiId && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '12px', 
                    background: '#f0fdf4', 
                    borderRadius: '8px',
                    textAlign: 'center'
                  }}>
                    <span style={{ color: '#166534', fontSize: '14px' }}>
                      <strong>UPI ID:</strong> {selectedEmployee.upiId}
                    </span>
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                onClick={() => document.getElementById('qr-preview-modal').style.display = 'none'}
                style={{
                  padding: '10px 24px',
                  background: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Payroll;